import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { NotificationRedirectService } from '../../services/notification_navigate.service';
import { NotificationResponse, Notify } from '../../models/notification.model';
import { UserTimezonePipe } from '../../core/pipes/timeZone.pipe';
import { NoDataFoundComponent } from '../../core/common_components/no-data-found/no-data-found.component';

type NotificationDrawerTabId = 'today' | 'week' | 'month' | 'all';

/** Maps a tab id to the `period` query param accepted by `notifications/myNotifications`. */
const PERIOD_BY_TAB: Record<NotificationDrawerTabId, string> = {
  all: 'all',
  today: 'today',
  week: 'this_week',
  month: 'this_month',
};

/** Notification list side-drawer opened from the header bell icon (replaces full-page navigation). */
@Component({
  selector: 'mindbase-notification-drawer',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, TranslateModule, MatIconModule, UserTimezonePipe, NoDataFoundComponent],
  templateUrl: './notification-drawer.component.html',
  styleUrl: './notification-drawer.component.scss',
})
export class NotificationDrawerComponent implements OnChanges, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly notificationNavigate = inject(NotificationRedirectService);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  readonly tabs: { id: NotificationDrawerTabId; label: string }[] = [
    { id: 'all', label: 'AllTime' },
    { id: 'today', label: 'Today' },
    { id: 'week', label: 'ThisWeek' },
    { id: 'month', label: 'ThisMonth' },
  ];

  private readonly limit = 15;

  activeTab: NotificationDrawerTabId = 'all';
  notifyData: Notify[] = [];
  loading = false;
  loadingMore = false;
  private page = 1;
  private totalCount = 0;

  get hasMore(): boolean {
    return this.notifyData.length < this.totalCount;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.activeTab = 'all';
      this.fetchPage(true);
    }
  }

  setActiveTab(tab: NotificationDrawerTabId): void {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.fetchPage(true);
  }

  onBodyScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
      this.loadMore();
    }
  }

  loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasMore) return;
    this.page++;
    this.fetchPage(false);
  }

  stripHtml(html: string): string {
    return html.replace(/<[^>]*>?/gm, '').trim();
  }

  onItemClick(item: Notify): void {
    const role = this.authService.currentProfileDetails?.role;
    const route = this.notificationNavigate.getNotificationUrl(item, role);
    this.close();
    this.router.navigateByUrl(`${route}`);
  }

  close(): void {
    this.closed.emit();
  }

  private fetchPage(reset: boolean): void {
    if (reset) {
      this.page = 1;
      this.notifyData = [];
      this.totalCount = 0;
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    const period = PERIOD_BY_TAB[this.activeTab];
    this.authService
      .getNotificationHistory(`period=${period}&page=${this.page}&limit=${this.limit}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: NotificationResponse) => {
          this.loading = false;
          this.loadingMore = false;
          this.notifyData = reset ? res.data : [...this.notifyData, ...res.data];
          this.totalCount = res.count;
        },
        error: () => {
          this.loading = false;
          this.loadingMore = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
